import { Injectable } from '@angular/core';
import { PartnerApi, LoopBackAuth, StandardDiscountApi, CommissionApi, VolumeDiscountApi, VolumeDiscountRateApi, DiscountLimitApi } from '../../assets/js/lb-sdk';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { MERCHANT_TYPE, AGENT_TYPE, AGENT_RELATIONSHIP, USER_ROLES, COMMISSION_LEVEL, SYSTEM } from '../enums';
import { Subject } from 'rxjs';
import { HasRolePipe } from '../pipes/has-role.pipe';
import { SapPaymentTypePipe } from '../pipes/sap-payment-type.pipe';
import { PaymentTypePipe } from '../pipes/payment-type.pipe';
import { MerchantTypePipe } from '../pipes/merchant-type.pipe';
import { SapMerchantTypePipe } from '../pipes/sap-merchant-type.pipe';

@Injectable({
  providedIn: 'root'
})
export class DiscountsService {
  private discounts;
  private standardDiscounts;
  private volumeDiscounts;
  private standardVolumeDiscounts;
  private commissions;
  private loading = {
    discounts: false,
    volumeDiscounts: false,
    commissions: false,
    standardDiscounts: false,
    standardVolumeDiscounts: false,
    maxDiscounts: false
  };
  discountsChange: Subject<Array<any>> = new Subject<Array<any>>();
  standardDiscountsChange: Subject<Array<any>> = new Subject<Array<any>>();
  volumeDiscountsChange: Subject<Array<any>> = new Subject<Array<any>>();
  standardVolumeDiscountsChange: Subject<Array<any>> = new Subject<Array<any>>();
  commissionsChange: Subject<Array<any>> = new Subject<Array<any>>();
  maxDiscountChange: Subject<Array<any>> = new Subject<Array<any>>();

  constructor(
    private partnerApi: PartnerApi,
    private standardDiscountApi: StandardDiscountApi,
    private standardVolumeDiscountApi: VolumeDiscountApi,
    private volumeDiscountApi: VolumeDiscountRateApi,
    private discountLimitApi: DiscountLimitApi,
    private commissionApi: CommissionApi,
    private toast: ToastrService,
    private hasRole: HasRolePipe,
    private sapPaymentTypePipe: SapPaymentTypePipe,
    private paymentTypePipe: PaymentTypePipe,
    private sapMerchantTypePipe: SapMerchantTypePipe,
    private merchantTypePipe: MerchantTypePipe,
    private loopBackAuth: LoopBackAuth
  ) {
    this.discountsChange.subscribe((value) => {
      this.discounts = value;
    });
    this.standardDiscountsChange.subscribe((value) => {
      this.standardDiscounts = value;
    });
    this.volumeDiscountsChange.subscribe((value) => {
      this.volumeDiscounts = value;
    });
    this.standardVolumeDiscountsChange.subscribe((value) => {
      this.standardVolumeDiscounts = value;
    });
    this.commissionsChange.subscribe((value) => {
      this.commissions = value;
    });
    this.maxDiscountChange.subscribe((value) => {
      SYSTEM.MAX_DISCOUNT = (value || [])
        .reduce((maxDiscounts, setup) => {
          maxDiscounts[setup.productType] = setup.value;
          return maxDiscounts;
        }, {});
    });
  }

  private fetchVolumeDiscounts() {
    const merchantRole = (this.getUser().roles || []).filter(role => {
      return role.principalType >= MERCHANT_TYPE.GENERAL_MERCHANT && role.principalType <= MERCHANT_TYPE.ONLINE_RETAILER;
    })[0];
    if (merchantRole) {
      this.loading.volumeDiscounts = true;
      this.volumeDiscountApi
        .find({
          where: {
            partnerId: this.getUser().partnerId,
            paymentType: merchantRole.paymentType,
            merchantType: merchantRole.principalType
          }
        })
        .pipe(finalize(() => {
          this.loading.volumeDiscounts = false;
        }))
        .subscribe(response => {
          this.volumeDiscountsChange.next(response);
        },
        error => {
          this.toast.error('An error occured while fetching the volume discounts. Try to reload the page or contact admin if error persist.', 'Volume Discounts Error');
        });
    }
  }

  private fetchStandardVolumeDiscounts(){
    const merchantRole = (this.getUser().roles || []).filter(role => {
      return role.principalType >= MERCHANT_TYPE.GENERAL_MERCHANT && role.principalType <= MERCHANT_TYPE.ONLINE_RETAILER;
    })[0];
    if (merchantRole) {
      this.loading.standardVolumeDiscounts = true;
      this.standardVolumeDiscountApi
        .find({
          where: {
            paymentType: merchantRole.paymentType,
            merchantType: merchantRole.principalType
          }
        })
        .pipe(finalize(() => {
          this.loading.standardVolumeDiscounts = false;
        }))
        .subscribe(response => {
          this.standardVolumeDiscountsChange.next(response);
        },
        error => {
          this.toast.error('An error occured while fetching the standard volume discounts. Try to reload the page or contact admin if error persist.', 'Standard Volume Discounts Error');
        });
    }
  }

  private fetchDiscounts() {
    this.loading.discounts = true;
    this.partnerApi
      .getDiscount(this.getUser().id, {}, false)
      .pipe(finalize(() => {
        this.loading.discounts = false;
      }))
      .subscribe(response => {
        this.discountsChange.next(response.filter(discount => {
          const role = this.getUser() && this.getUser().roles && this.getUser().roles[0];
          return role && role.role &&
            this.merchantTypePipe.transform(role.role.code) === this.sapMerchantTypePipe.transform(discount.merchantType) &&
            this.paymentTypePipe.transform(role.paymentType) === this.sapPaymentTypePipe.transform(discount.paymentType);
        }));
      },
      error => {
        this.toast.error('An error occured while fetching the discounts. Try to reload the page or contact admin if error persist.', 'Discounts Error');
      });
  }

  private fetchStandardDiscounts() {
    const merchantRole = (this.getUser().roles || []).filter(role => {
      return role.principalType >= MERCHANT_TYPE.GENERAL_MERCHANT && role.principalType <= MERCHANT_TYPE.ONLINE_RETAILER;
    })[0];
    if (merchantRole) {
      this.loading.standardDiscounts = true;
      this.standardDiscountApi
        .find({
          where: {
            paymentType: merchantRole.paymentType,
            merchantType: merchantRole.principalType
          }
        })
        .pipe(finalize(() => {
          this.loading.standardDiscounts = false;
        }))
        .subscribe(response => {
          this.standardDiscountsChange.next(response);
        },
        error => {
          this.toast.error('An error occured while fetching the standard discounts. Try to reload the page or contact admin if error persist.', 'Standard Discounts Error');
        });
    }
  }

  fetchCommissions() {
    const agentRole = (this.getUser().roles || []).filter(role => {
      return role.principalType >= AGENT_TYPE.AUH && role.principalType <= AGENT_TYPE.BROKER;
    })[0];
    this.loading.commissions = true;
    this.commissionApi
      .find({
        where: {
          relationshipType: AGENT_RELATIONSHIP[AGENT_TYPE[agentRole.principalType]]
        }
      })
      .pipe(finalize(() => {
        this.loading.commissions = false;
      }))
      .subscribe(response => {
        this.commissionsChange.next(response.filter((commission: any) => {
          const commissionLevel = [
            AGENT_TYPE[agentRole.principalType],
            this.getUser() &&
            this.getUser().merchantDetails &&
            this.getUser().merchantDetails.levelSetup ||
            ''
          ].join('');
          return commission.commissionLevel === COMMISSION_LEVEL[commissionLevel];
        }));
      },
      error => {
        this.toast.error('An error occured while fetching the commissions. Try to reload the page or contact admin if error persist.', 'Commissions Error');
      });
  }

  fetchMaxDiscounts() {
    this.loading.maxDiscounts = true;
    this.discountLimitApi
      .find({})
      .pipe(finalize(() => {
        this.loading.maxDiscounts = false;
      }))
      .subscribe(response => {
        this.maxDiscountChange.next(response);
      },
      error => {
        this.toast.error('An error occured while fetching the max discounts. Try to reload the page or contact admin if error persist.', 'Max Discounts Error');
      });
  }

  loadDiscounts() {
    if (
      this.hasRole.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, '>=') &&
      this.hasRole.transform(this.getUser().roles, USER_ROLES.AUH, '<')
    ) {
      this.fetchDiscounts();
      this.fetchStandardDiscounts();
      this.fetchVolumeDiscounts();
      this.fetchStandardVolumeDiscounts();
      this.fetchMaxDiscounts();
    }
    if (this.hasRole.transform(this.getUser().roles, USER_ROLES.AUH, 'between', USER_ROLES.BROKER)) {
      this.fetchCommissions();
    }
  }

  getDiscounts() {
    return this.discounts;
  }

  getStandardDiscounts() {
    return this.standardDiscounts;
  }

  getVolumeDiscounts() {
    return this.volumeDiscounts;
  }

  getStandardVolumeDiscounts() {
    return this.standardVolumeDiscounts;
  }

  getCommissions() {
    return this.commissions;
  }

  getDiscountByProduct(product) {
    if (product && product.productType && product.productType.name) {
      let discount = (this.discounts || [])
        .filter(discount => {
          return discount.productType === product.productType.name;
        })[0];
      if (!discount) {
        discount = (this.standardDiscounts || [])
          .filter(standardDiscount => {
            return standardDiscount.productType === product.productType.name;
          })
          .map(standardDiscount => {
            return {
              rate: standardDiscount.discount,
              unit: 'P' // DEFAULTS TO PERCENT
            }
          })[0];
      }
      if (!discount) {
        discount = (this.commissions || [])
          .filter(commission => {
            return commission.productType === product.productType.name;
          })[0];
      }
      return discount;
    } else {
      return undefined
    }
  }

  getVolumeDiscountByProduct(product, orders) {
    if (product && product.productType && product.productType.name && orders) {
      var quantity = (product && product.productType && product.productType.products)
        .reduce((total, p) => {
          return total + (orders[product.productType.name + p.planTypeCode] || 0);
        }, 0);
      let volumeDiscount = (this.volumeDiscounts || [])
        .filter(discount => {
          return discount.productType === product.productType.name;
        })
        .sort((a, b) => {
          return a.minimum - b.minimum;
        })
        .reduce((match, discount) => {
          return (quantity >= discount.minimum) ? discount : match;
        }, undefined);
      if (!volumeDiscount) {
        volumeDiscount = (this.standardVolumeDiscounts || [])
          .filter(standardVolumeDiscount => {
            return standardVolumeDiscount.productType === product.productType.name;
          })
          .sort((a, b) => {
            return a.minimum - b.minimum;
          })
          .reduce((match, discount) => {
            return (quantity >= discount.minimum) ? discount : match;
          }, undefined);
      }
      return volumeDiscount;
    } else {
      return undefined;
    }
  }

  isLoading(property?:string):boolean {
    return property ?
      this.loading[property] :
      Object.keys(this.loading).reduce((loading, key):boolean => {
        return loading || this.loading[key];
      }, false);
  }

  getUser() {
    return this.loopBackAuth.getToken() ? this.loopBackAuth.getCurrentUserData() : undefined;
  }
}
